import { Alert, Box, Button, FormControl, FormLabel, IconButton, Input, Modal, ModalClose, ModalDialog, ModalOverflow, Option, Select, Sheet, Stack, Textarea, Typography } from "@mui/joy";
import { useContext, useEffect, useState } from "react";

import { motion, AnimatePresence } from "framer-motion";
import { LayoutContext } from "../context";
import { APICreateComplaint } from "../api";


// Причины жалоб (соответствуют CHOICES_COMPLAINT_REASONS из models.py)
const COMPLAINT_REASONS = [
  { value: 0, label: "Не соответствие фотографии" },
  { value: 1, label: "Не соответствие описания" },
  { value: 2, label: "Не соответствие дохода" },
  { value: 3, label: "Не соответствие возраста" },
  { value: 4, label: "Жестокое обращение" },
  { value: 5, label: "Наркотики" }
];


export default function ComplaintModal() {
    const { deviceType, modalComplaint, setModalComplaint, token } = useContext(LayoutContext);
    const [formData, setFormData] = useState({
        reason: '',
        description: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success' или 'error'

    useEffect(() => {
        if (modalComplaint.open) {
            // Сброс формы при открытии
            setFormData({
                reason: '',
                description: ''
            });
            setSubmitStatus(null);
        }
    }, [modalComplaint.open]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async () => {
        if (formData.reason == null || formData.reason == undefined) {
            setSubmitStatus('error');
            return;
        }

        setIsSubmitting(true);
        try {
            await APICreateComplaint(token, {
                complained_user: modalComplaint.user?.id,
                reason: parseInt(formData.reason),
                description: formData.description
            });
            
            setSubmitStatus('success');
            // Закрываем модальное окно через 2 секунды после успешной отправки
            setTimeout(() => {
                setModalComplaint({ open: false, user: null });
            }, 2000);
        } catch (error) {
            console.error('Ошибка при отправке жалобы:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setModalComplaint({ open: false, user: null });
    };

    return (
        <AnimatePresence>
            {modalComplaint?.open && (
                <Modal
                    sx={{ zIndex: 10002 }}
                    open={modalComplaint?.open}
                    slotProps={{
                        backdrop: {
                            component: motion.div,
                            initial: { opacity: 0, backdropFilter: 'blur(0px)' },
                            animate: { opacity: 1, backdropFilter: 'blur(8px)' },
                            exit: { opacity: 0, backdropFilter: 'blur(0px)' },
                            transition: { duration: 0.3, ease: "easeInOut" },
                        }
                    }}
                >
                    <ModalOverflow sx={{ height: 'auto' }}>
                        <ModalDialog
                            size="lg"
                            component={motion.div}
                            initial={{ opacity: 0, y: -10, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.98 }}
                            transition={{ duration: 0.2, ease: "easeInOut" }}
                            sx={(theme) => ({
                                [theme.breakpoints.up("xl")]: { width: '550px', mx: 0 },
                                [theme.breakpoints.down("xl")]: { width: '550px', mx: 0 },
                                [theme.breakpoints.down("lg")]: { width: '550px', mx: 0 },
                                [theme.breakpoints.down("md")]: { width: '450px', mx: 0 },
                                [theme.breakpoints.down("sm")]: { width: '95%', p: 3 },
                                width: '100%'
                            })}
                        >
                            <Box sx={{ mt: deviceType == 'Phone' ? 10 : 0 }}></Box>

                            <ModalClose sx={{ mt: deviceType == 'Phone' ? 10 : 0 }} onClick={handleClose}/>
                            
                            <Typography level="h3" component="h2" sx={{ mb: 2 }}>
                                Пожаловаться на пользователя
                            </Typography>
                            
                            {modalComplaint.user && (
                                <Alert color="neutral" sx={{ mb: 2 }}>
                                    <Typography level="body-sm">
                                        Жалоба на: {modalComplaint.user.full_name || 'Пользователь'}
                                    </Typography>
                                </Alert>
                            )}

                            {submitStatus === 'success' ? (
                                <Alert color="success" sx={{ mb: 2 }}>
                                    <Typography level="body-sm">
                                        Спасибо за вашу жалобу! Мы рассмотрим её в ближайшее время.
                                    </Typography>
                                </Alert>
                            ) : (
                                <>
                                    <FormControl sx={{ mb: 2 }}>
                                        <FormLabel>Причина жалобы *</FormLabel>
                                        <Select
                                            slotProps={{ listbox: { sx: { zIndex: 10010 } } }}
                                            value={formData.reason}
                                            onChange={(_, value) => handleInputChange('reason', value)}
                                            placeholder="Выберите причину"
                                        >
                                            {COMPLAINT_REASONS.map(reason => (
                                                <Option key={reason.value} value={reason.value}>
                                                    {reason.label}
                                                </Option>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <FormControl sx={{ mb: 3 }}>
                                        <FormLabel>Подробное описание</FormLabel>
                                        <Textarea
                                            minRows={3}
                                            placeholder="Опишите подробно ситуацию..."
                                            value={formData.description}
                                            onChange={(e) => handleInputChange('description', e.target.value)}
                                        />
                                    </FormControl>

                                    {submitStatus === 'error' && (
                                        <Alert color="danger" sx={{ mb: 2 }}>
                                            <Typography level="body-sm">
                                                Пожалуйста, заполните обязательные поля и попробуйте снова.
                                            </Typography>
                                        </Alert>
                                    )}

                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                        <Button variant="outlined" onClick={handleClose}>
                                            Отмена
                                        </Button>
                                        <Button 
                                            loading={isSubmitting}
                                            onClick={handleSubmit}
                                            disabled={formData.reason == null || formData.reason == undefined}
                                        >
                                            Отправить жалобу
                                        </Button>
                                    </Stack>
                                </>
                            )}
                        </ModalDialog>
                    </ModalOverflow>
                </Modal>
            )}
        </AnimatePresence>
    );
}